<template>
  <div class="chat-msg" :class="type === 'assistant' ? 'msg-ai' : ''">

    <!-- Loading -->
    <div v-if="type === 'loading'" class="flex justify-start mb-4">
      <div class="bg-gray-100 rounded-2xl rounded-bl-sm py-3 px-5 flex items-center gap-2">
        <span class="loading-dot"></span>
        <span class="loading-dot delay-1"></span>
        <span class="loading-dot delay-2"></span>
        <span class="ml-1 text-[13px] text-gray-400">{{ text || 'AI 답변 생성중' }}</span>
      </div>
    </div>

    <!-- User -->
    <div v-else-if="type !== 'assistant'" class="flex justify-end mb-4">
      <div class="max-w-[80%] bg-slate-700 text-white rounded-2xl rounded-br-sm py-2.5 px-4 text-[14px] whitespace-pre-wrap break-words leading-[1.7]">
        {{ formattedText }}
      </div>
    </div>

    <!-- Assistant -->
    <div v-else class="flex justify-start mb-4">
      <div class="max-w-[85%] bg-gray-100 text-gray-800 rounded-2xl rounded-bl-sm py-2.5 px-4 text-left">

      <!-- Image -->
      <img v-if="isImage" :src="imageUrl" :alt="imageUrl" class="rounded-xl max-w-[500px] max-h-[500px] object-contain" />

      <!-- WordCloud -->
      <div v-else-if="hasWordCloudData">
        <div class="flex items-center gap-3 mb-2">
          <span class="text-[14px] text-gray-700 leading-[1.7]">{{ wordCloudResponse }}</span>
          <a href="#" @click.prevent="downloadCsv" class="text-[11px] text-gray-400 hover:text-gray-600 border border-gray-200 rounded-md px-2 py-0.5 transition-colors">CSV</a>
        </div>
        <div v-if="!showWordCloud">
          <button @click="showWordCloud = true" class="text-[13px] text-violet-600 hover:underline">워드클라우드 보기</button>
        </div>
        <div v-else class="bg-gray-50 rounded-xl p-3 mt-2" style="width:600px;height:400px;">
          <canvas ref="wordCloudCanvas"></canvas>
        </div>
      </div>

      <!-- Chart -->
      <div v-else-if="hasChartData">
        <div class="flex items-center gap-3 mb-2">
          <span class="text-[14px] text-gray-700 leading-[1.7]">{{ chartResponse }}</span>
          <a href="#" @click.prevent="downloadCsv" class="text-[11px] text-gray-400 hover:text-gray-600 border border-gray-200 rounded-md px-2 py-0.5 transition-colors">CSV</a>
        </div>
        <div v-if="!showChart">
          <button @click="showChart = true" class="text-[13px] text-violet-600 hover:underline">차트 보기</button>
        </div>
        <div v-else class="bg-gray-50 rounded-xl p-3 mt-2" style="width:600px;height:300px;">
          <Line :data="extractedChartData" :options="chartOptions" />
        </div>
      </div>

      <!-- Markdown / JSON -->
      <div v-else-if="isJsonResponse" class="msg-text markdown-content" v-html="formattedResponse"></div>

      <!-- Plain Text -->
      <div v-else class="msg-text whitespace-pre-wrap break-words" :class="isError ? 'text-red-600' : ''">
        <span v-html="formattedTextHtml"></span><span v-if="streaming" class="streaming-cursor">|</span>
      </div>

      <!-- Feedback -->
      <div v-if="!streaming" class="feedback flex items-center gap-1 mt-2">
        <button @click="togglePositive" class="p-1 rounded transition-colors" :class="isPositiveClicked ? 'text-gray-700' : 'text-gray-300 hover:text-gray-500'">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"/></svg>
        </button>
        <button @click="toggleNegative" class="p-1 rounded transition-colors" :class="isNegativeClicked ? 'text-gray-700' : 'text-gray-300 hover:text-gray-500'">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3zm7-13h2.67A2.31 2.31 0 0 1 22 4v7a2.31 2.31 0 0 1-2.33 2H17"/></svg>
        </button>
      </div>
    </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch, nextTick, onMounted } from 'vue'
import { marked } from 'marked'
import { Line } from 'vue-chartjs'
import {
  Chart as ChartJS, Title, Tooltip, Legend, BarElement, LineElement, PointElement, CategoryScale, LinearScale,
} from 'chart.js'
import { registerables } from 'chart.js'
import { WordCloudController, WordElement } from 'chartjs-chart-wordcloud'

ChartJS.register(Title, Tooltip, Legend, BarElement, LineElement, PointElement, CategoryScale, LinearScale)
ChartJS.register(...registerables, WordCloudController, WordElement)

onMounted(() => {
  const timer = setTimeout(() => {
    if (document.querySelectorAll(".markdown-content").length > 0) {
      document.querySelectorAll(".markdown-content a").forEach(link => {
        link.setAttribute("target", "_blank");
        link.setAttribute("rel", "noopener noreferrer");
      });
    }
    if (wordCloudCanvas.value && hasWordCloudData.value && showWordCloud.value) renderWordCloud();
  }, 100);
  return () => clearTimeout(timer);
});

const props = defineProps({
  type: { type: String, default: 'user' },
  text: { type: String, required: true, default: '' },
  isError: { type: Boolean, default: false },
  streaming: { type: Boolean, default: false }
})

const showChart = ref(false)
const showWordCloud = ref(false)
const wordCloudCanvas = ref(null)
const wordCloudChart = ref(null)

const hasWordCloudData = computed(() => {
  try { if (props.type === 'user') return false; const d = typeof props.text === 'string' ? JSON.parse(props.text) : props.text; return d?.chartData?.words && Array.isArray(d.chartData.words); } catch { return false; }
});
const wordCloudResponse = computed(() => {
  if (!hasWordCloudData.value) return ''; try { const d = typeof props.text === 'string' ? JSON.parse(props.text) : props.text; return d.Response || "워드클라우드 데이터"; } catch { return "워드클라우드 데이터"; }
});
const hasChartData = computed(() => {
  try { if (props.type === 'user') return false; const d = typeof props.text === 'string' ? JSON.parse(props.text) : props.text; return d?.chartData?.datasets; } catch { return false; }
});
const chartResponse = computed(() => {
  if (!hasChartData.value) return ''; try { const d = typeof props.text === 'string' ? JSON.parse(props.text) : props.text; return d.Response || "차트 데이터"; } catch { return "차트 데이터"; }
});
const extractedChartData = computed(() => {
  if (!hasChartData.value) return null; try { const d = typeof props.text === 'string' ? JSON.parse(props.text) : props.text; return d.chartData; } catch { return null; }
});
const chartOptions = ref({ responsive: true, maintainAspectRatio: false, scales: { y: { beginAtZero: true } } });

const isImage = computed(() => {
  try { const c = typeof props.text === 'string' ? props.text : props.text?.Response || props.text?.content || props.text; const t = typeof c === 'string' ? c : JSON.stringify(c); return t.includes('https://') && (t.includes('/functionImage/') || t.endsWith('.png')); } catch { return false; }
})
const imageUrl = computed(() => {
  if (!isImage.value) return ''; const c = typeof props.text === 'string' ? props.text : props.text?.Response || props.text?.content || props.text; const m = c.match(/(https:\/\/[^\s"]+)/); return m ? m[0] : '';
})
const isJsonResponse = computed(() => {
  try { if (hasChartData.value) return false; if (props.type == 'user') return false; if (typeof props.text !== 'string') return true; JSON.parse(props.text); return true; } catch { return false; }
});

marked.setOptions({ breaks: true, gfm: true });

const renderer = new marked.Renderer();
const originalLinkRenderer = renderer.link;
renderer.link = function({ href, title, text }) {
  const isLongUrl = text && /^https?:\/\/.{40,}/.test(text);
  const displayText = isLongUrl ? shortenUrl(text) : text;
  const titleAttr = title ? ` title="${title}"` : '';
  return `<a href="${href}" target="_blank" rel="noopener noreferrer"${titleAttr}>${displayText}</a>`;
};
marked.use({ renderer });

const formattedResponse = computed(() => {
  try {
    let data = props.text;
    if (typeof data === "string") { try { data = JSON.parse(data); } catch { return `<div class="markdown-content">${marked(data.replace(/\n/g, "<br>"))}</div>`; } }
    if (data.Response && typeof data.Response === "string") {
      const html = marked.parse(data.Response.replace(/```markdown/g, "").replace(/```/g, ""));
      return `<div class="markdown-content">${html}</div>`;
    }
    if (data.Response && Array.isArray(data.Response)) {
      const items = data.Response;
      if (items.length > 0 && 'Channel' in items[0] && 'Count' in items[0]) {
        const total = items.reduce((s, i) => s + i.Count, 0);
        return `총 ${total.toLocaleString()}건<br><br>` + items.sort((a,b) => b.Count - a.Count).map(i => `${i.Channel.replace('_',' ')}: ${i.Count.toLocaleString()}건`).join('<br>');
      }
    }
    return `<div class="markdown-content">${marked("```json\n" + JSON.stringify(data, null, 2) + "\n```")}</div>`;
  } catch { return `<div class="markdown-content">${marked(String(props.text))}</div>`; }
});

const isPositiveClicked = ref(false)
const isNegativeClicked = ref(false)
const togglePositive = () => { isPositiveClicked.value = !isPositiveClicked.value; if (isPositiveClicked.value) isNegativeClicked.value = false; }
const toggleNegative = () => { isNegativeClicked.value = !isNegativeClicked.value; if (isNegativeClicked.value) isPositiveClicked.value = false; }

const formattedText = computed(() => {
  if (typeof props.text === 'string') return props.text;
  try { if (props.text?.Response && typeof props.text.Response === 'string') return props.text.Response; return JSON.stringify(props.text, null, 2); } catch { return String(props.text); }
});

function shortenUrl(url) {
  try {
    const u = new URL(url);
    let short = u.hostname + u.pathname;
    if (short.endsWith('/')) short = short.slice(0, -1);
    return short;
  } catch { return url; }
}

function escapeHtml(str) {
  return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

const formattedTextHtml = computed(() => {
  const text = formattedText.value;
  const escaped = escapeHtml(text);
  return escaped.replace(/(https?:\/\/[^\s<"]+)/g, (url) => {
    const display = shortenUrl(url.replace(/&amp;/g, '&'));
    return `<a href="${url}" target="_blank" rel="noopener noreferrer" class="inline-flex items-center gap-1 text-violet-600 hover:text-violet-800 underline underline-offset-2 break-all">${escapeHtml(display)}</a>`;
  });
});

// WordCloud
const wordCloudChartData = computed(() => {
  if (!hasWordCloudData.value) return null;
  try { const d = typeof props.text === 'string' ? JSON.parse(props.text) : props.text; const w = d.chartData.words; const vals = w.map(x => x.count); if (!vals.length) return { labels: [], datasets: [{ data: [] }] }; const mx = Math.max(...vals), mn = Math.min(...vals); return { labels: w.map(x => x.word), datasets: [{ data: w.map(x => mn === mx ? 500 : Math.floor(((x.count - mn) / (mx - mn)) * 200)) }] }; } catch { return null; }
});
const wordCloudOptions = ref({ responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false }, tooltip: { callbacks: { label: (c) => `${c.label}: ${c.raw}` } } }, color: ['#FF6384','#36A2EB','#FFCE56','#4BC0C0','#9966FF','#FF9F40'], fit: true });
const renderWordCloud = () => {
  if (wordCloudChart.value) { wordCloudChart.value.destroy(); wordCloudChart.value = null; }
  const ctx = wordCloudCanvas.value?.getContext('2d'); if (!ctx) return;
  try { wordCloudChart.value = new ChartJS(ctx, { type: WordCloudController.id, data: wordCloudChartData.value, options: wordCloudOptions.value }); } catch (e) { console.error('WordCloud error:', e); }
};
watch([() => wordCloudChartData.value, () => showWordCloud.value], ([d, f]) => { if (d && f) nextTick(() => renderWordCloud()); }, { deep: true, immediate: true });

// CSV
const escapeCSV = (v) => { const s = String(v); return (s.includes(',') || s.includes('"') || s.includes('\n')) ? `"${s.replace(/"/g, '""')}"` : s; };
const convertChartDataToCsv = () => {
  try {
    if (hasChartData.value && extractedChartData.value) { const d = extractedChartData.value; let c = ['Category', ...d.datasets.map(ds => ds.label || 'Value')].join(',') + '\n'; d.labels.forEach((l, i) => { c += [l, ...d.datasets.map(ds => escapeCSV(ds.data[i]))].join(',') + '\n'; }); return c; }
    else if (hasWordCloudData.value) { const d = typeof props.text === 'string' ? JSON.parse(props.text) : props.text; let c = 'Word,Frequency\n'; d.chartData.words.forEach(w => { c += `${w.text},${escapeCSV(w.value)}\n`; }); return c; }
    return null;
  } catch { return null; }
};
const downloadCsv = () => {
  const csv = convertChartDataToCsv(); if (!csv) return;
  const blob = new Blob(["\uFEFF" + csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a'); link.href = URL.createObjectURL(blob);
  const n = new Date(); const ts = `${n.getFullYear()}${(n.getMonth()+1).toString().padStart(2,'0')}${n.getDate().toString().padStart(2,'0')}_${n.getHours().toString().padStart(2,'0')}${n.getMinutes().toString().padStart(2,'0')}`;
  link.download = `${hasChartData.value ? 'chart' : 'wordcloud'}_${ts}.csv`;
  link.style.visibility = 'hidden'; document.body.appendChild(link); link.click(); document.body.removeChild(link);
};
</script>

<style lang="scss" scoped>
.msg-text {
  font-size: 14px;
  line-height: 1.75;
  color: #333;
}

.chat-msg.msg-ai .feedback {
  opacity: 0;
  transition: opacity 0.15s;
}
.chat-msg.msg-ai:hover .feedback {
  opacity: 1;
}

// Markdown
::v-deep(.markdown-content) { display: block; white-space: normal; word-break: break-word; overflow-wrap: break-word; line-height: 1.75; font-size: 14px; color: #333; }
::v-deep(.markdown-content h1) { font-size: 1.3rem; font-weight: 700; margin: 18px 0 8px; color: #111; }
::v-deep(.markdown-content h2) { font-size: 1.15rem; font-weight: 600; margin: 16px 0 6px; color: #222; }
::v-deep(.markdown-content h3) { font-size: 1rem; font-weight: 600; margin: 14px 0 4px; color: #333; }
::v-deep(.markdown-content p) { margin-bottom: 8px; }
::v-deep(.markdown-content ul) { margin-left: 4px; padding-left: 20px; list-style-type: disc; }
::v-deep(.markdown-content ol) { margin-left: 4px; padding-left: 20px; list-style-type: decimal; }
::v-deep(.markdown-content li) { margin-bottom: 3px; line-height: 1.7; }
::v-deep(.markdown-content a) { color: #7c3aed; text-decoration: underline; text-underline-offset: 2px; }
::v-deep(.markdown-content code) { background: #f5f5f0; padding: 1px 5px; border-radius: 4px; font-size: 13px; }
::v-deep(.markdown-content pre) { background: #2b2b2b; color: #e6e6e6; padding: 14px 18px; border-radius: 10px; overflow-x: auto; margin: 10px 0; font-size: 13px; }
::v-deep(.markdown-content pre code) { background: none; padding: 0; color: inherit; }
::v-deep(.markdown-content table) { width: 100%; border-collapse: collapse; margin: 10px 0; font-size: 13px; border: 1px solid #e5e5e0; }
::v-deep(.markdown-content th) { background: #fafaf8; font-weight: 600; color: #555; padding: 8px 12px; border: 1px solid #e5e5e0; text-align: left; }
::v-deep(.markdown-content td) { padding: 7px 12px; border: 1px solid #ececea; }
::v-deep(.markdown-content th:nth-child(1)), ::v-deep(.markdown-content th:nth-child(2)), ::v-deep(.markdown-content td:nth-child(1)), ::v-deep(.markdown-content td:nth-child(2)) { white-space: normal; word-break: break-word; }
::v-deep(.markdown-content br) { display: block; content: ""; margin-top: 3px; }

// Loading dots
@keyframes dot-bounce {
  0%, 80%, 100% { transform: scale(0.4); opacity: 0.3; }
  40% { transform: scale(1); opacity: 1; }
}
.loading-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background-color: #9ca3af;
  animation: dot-bounce 1.4s infinite ease-in-out both;
}
.loading-dot.delay-1 { animation-delay: 0.16s; }
.loading-dot.delay-2 { animation-delay: 0.32s; }

// Streaming cursor
@keyframes blink {
  0%, 100% { opacity: 1; }
  50% { opacity: 0; }
}
.streaming-cursor {
  display: inline;
  color: #7c3aed;
  font-weight: 600;
  animation: blink 0.8s step-end infinite;
}
</style>
